import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Scale,
  Languages,
  FileSearch,
  FileText,
  SpellCheck,
  Info,
  AlignLeft,
  ArrowLeftRight,
  List,
  GitCompare,
  CheckSquare,
  MessageSquarePlus,
} from 'lucide-react';
import { directionForLanguage } from '../../i18n/config';
import './SolutionsGrid.css';

export type SolutionId = 'master_cross_checks' | 'construction_loan';

export type GenericSolutionId =
  | 'legal_translation'
  | 'favor_a_party'
  | 'contract_review'
  | 'legal_tone'
  | 'language_check'
  | 'explain'
  | 'tldr'
  | 'make_mutual'
  | 'summary'
  | 'redline_analyzer'
  | 'make_standard'
  | 'enhance_argumentation';

interface SolutionCube {
  id: SolutionId;
  icon: React.ReactNode;
}

interface GenericSolutionCube {
  id: GenericSolutionId;
  icon: React.ReactNode;
}

const COURTYARD_SOLUTIONS: SolutionCube[] = [
  {
    id: 'master_cross_checks',
    icon: <Scale className="solution-cube-icon" size={22} strokeWidth={1.5} />,
  },
  {
    id: 'construction_loan',
    icon: <FileText className="solution-cube-icon" size={22} strokeWidth={1.5} />,
  },
];

const GENERIC_SOLUTIONS: GenericSolutionCube[] = [
  { id: 'legal_translation', icon: <Languages className="solution-cube-icon" size={20} strokeWidth={1.5} /> },
  { id: 'favor_a_party', icon: <Scale className="solution-cube-icon" size={20} strokeWidth={1.5} /> },
  { id: 'contract_review', icon: <FileSearch className="solution-cube-icon" size={20} strokeWidth={1.5} /> },
  { id: 'legal_tone', icon: <FileText className="solution-cube-icon" size={20} strokeWidth={1.5} /> },
  { id: 'language_check', icon: <SpellCheck className="solution-cube-icon" size={20} strokeWidth={1.5} /> },
  { id: 'explain', icon: <Info className="solution-cube-icon" size={20} strokeWidth={1.5} /> },
  { id: 'tldr', icon: <AlignLeft className="solution-cube-icon" size={20} strokeWidth={1.5} /> },
  { id: 'make_mutual', icon: <ArrowLeftRight className="solution-cube-icon" size={20} strokeWidth={1.5} /> },
  { id: 'summary', icon: <List className="solution-cube-icon" size={20} strokeWidth={1.5} /> },
  { id: 'redline_analyzer', icon: <GitCompare className="solution-cube-icon" size={20} strokeWidth={1.5} /> },
  { id: 'make_standard', icon: <CheckSquare className="solution-cube-icon" size={20} strokeWidth={1.5} /> },
  { id: 'enhance_argumentation', icon: <MessageSquarePlus className="solution-cube-icon" size={20} strokeWidth={1.5} /> },
];

interface SolutionsGridProps {
  onSelect: (id: SolutionId) => void;
  onSelectGeneric?: (id: GenericSolutionId) => void;
}

export const SolutionsGrid: React.FC<SolutionsGridProps> = ({ onSelect, onSelectGeneric }) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);

  return (
    <div className="solutions-grid-container" dir={chromeDir}>
      <section className="solutions-section">
        <h3 className="solutions-section-title">{t('solutions.courtyardSection')}</h3>
        <div className="solutions-grid">
          {COURTYARD_SOLUTIONS.map((solution) => (
            <button
              key={solution.id}
              type="button"
              className="solution-cube"
              onClick={() => onSelect(solution.id)}
            >
              <span className="solution-cube-icon-wrap">{solution.icon}</span>
              <span className="solution-cube-label">
                {t(`solutions.courtyard.${solution.id}`)}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="solutions-section">
        <h3 className="solutions-section-title">{t('solutions.genericSection')}</h3>
        <div className="solutions-grid solutions-grid-generic">
          {GENERIC_SOLUTIONS.map((solution) => (
            <button
              key={solution.id}
              type="button"
              className="solution-cube solution-cube-generic"
              onClick={() => onSelectGeneric?.(solution.id)}
              title={onSelectGeneric ? undefined : t('solutions.comingSoon')}
            >
              <span className="solution-cube-icon-wrap">{solution.icon}</span>
              <span className="solution-cube-label">
                {t(`solutions.generic.${solution.id}`)}
              </span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};
